import React, { useState } from 'react'
import { Formik, Form, FieldArray } from 'formik'
import * as Yup from 'yup'
import TextInput from '../../../shared/TextInput'
import DropdownField from '../../../shared/DropdownField'
import { useTranslation } from 'react-i18next'
import CustomButton from '../../../shared/CustomButton'
import {
  FaSearch,
  FaPlus,
  FaTimes,
  FaArrowCircleLeft,
  FaArrowCircleRight
} from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import CheckPermission from '../../../hooks/CheckPermission'

const validationSchema = Yup.object({
  vendor: Yup.string().required('Vendor is required'),
  grNo: Yup.string().required('GR No. is required'),
  creditNoteNo: Yup.string().required('Credit Note No. is required')
})

const ReturnToVendorForm = () => {
  const { hasPermission } = CheckPermission()
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [items, setItems] = useState([])
  const [searched, setSearched] = useState(false)

  const calculateTotals = item => {
    const subtotal = item.returnRate * item.quantity
    const totalAmount =
      subtotal - item.returnDiscountAmt + item.returnVatAmt + item.returnCcAmt

    return {
      ...item,
      subtotal,
      totalAmount
    }
  }

  const handleSearch = () => {
    const fakeItems = [
      {
        itemName: 'Towel',
        batchNo: '',
        grnNo: 2,
        availableQty: 50,
        itemRate: 20,
        returnRate: 0,
        quantity: 0,
        subtotal: 0,
        returnDiscountAmt: 0,
        returnVatAmt: 0,
        returnCcAmt: 0,
        totalAmount: 0,
        remark: ''
      }
    ]
    setItems(fakeItems)
    setSearched(true)
  }

  const handleAddItem = () => {
    const newItem = {
      itemName: '',
      batchNo: '',
      grnNo: '',
      availableQty: 0,
      itemRate: 0,
      returnRate: 0,
      quantity: 0,
      subtotal: 0,
      returnDiscountAmt: 0,
      returnVatAmt: 0,
      returnCcAmt: 0,
      totalAmount: 0,
      remark: ''
    }
    setItems(prev => [...prev, newItem])
  }

  const handleRemoveItem = index => {
    if (index != 0) {
      setItems(prev => prev.filter((_, i) => i !== index))
    }
  }

  const handleItemChange = (index, field, value) => {
    setItems(prev =>
      prev.map((item, i) => {
        if (i === index) {
          const updated = { ...item, [field]: value }
          return calculateTotals(updated)
        }
        return item
      })
    )
  }

  return (
    <div className='main-page-container'>
      <div className='flex justify-between items-center'>
        <div className='flex items-center mb-3'>
          {i18n.language === 'en' ? (
            <FaArrowCircleLeft
              className='text-primary cursor-pointer me-2'
              size={20}
              onClick={() => navigate(-1)}
            />
          ) : (
            <FaArrowCircleRight
              className='text-primary cursor-pointer me-2'
              size={20}
              onClick={() => navigate(-1)}
            />
          )}
          <h3 className='text-[16px] text-darkBg font-[600] lg:text-[20px] md:text-[18px]'>
            {t('ReturnToVendor')}
          </h3>
        </div>
      </div>

      <Formik
        initialValues={{
          fiscalYear: '2025',
          vendor: '',
          grNo: '',
          creditNoteNo: '1'
        }}
        validationSchema={validationSchema}
        onSubmit={() => {
          handleSearch()
        }}
      >
        {() => (
          <Form>
            <div className='grid grid-cols-1 gap-4 md:grid-cols-5'>
              <TextInput
                name='fiscalYear'
                label={t('FiscalYear')}
                placeholder={t('FiscalYear')}
              />
              <DropdownField
                name='vendor'
                label={t('Vendor')}
                placeholder={t('SelectVendor')}
                options={[
                  { value: 'vendor1', label: 'Vendor 1' },
                  { value: 'vendor2', label: 'Vendor 2' }
                ]}
              />
              <TextInput
                name='grNo'
                label={t('GRNo.')}
                placeholder={t('GRNo.')}
                type='number'
              />
              <TextInput
                name='creditNoteNo'
                label={t('CreditNoteNo.')}
                placeholder={t('CreditNoteNo.')}
                type='number'
                disabled
              />
              <div className='flex items-end'>
                <CustomButton
                  type='submit'
                  label={t('Search')}
                  icon={FaSearch}
                />
              </div>
            </div>
            {searched && (
              <div className='mt-6 space-y-6'>
                {items.map((item, index) => (
                  <div
                    key={index}
                    className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3 p-4 border rounded relative'
                  >
                    <FaTimes
                      className='absolute top-2 right-2 text-red-500 cursor-pointer'
                      onClick={() => handleRemoveItem(index)}
                    />
                    <TextInput
                      name={`itemName-${index}`}
                      label='Item Name'
                      value={item.itemName}
                      onChange={e =>
                        handleItemChange(index, 'itemName', e.target.value)
                      }
                    />
                    <TextInput
                      name={`batchNo-${index}`}
                      label='Batch No'
                      value={item.batchNo}
                      onChange={e =>
                        handleItemChange(index, 'batchNo', e.target.value)
                      }
                    />
                    <TextInput
                      name={`returnRate-${index}`}
                      label='Return Rate'
                      type='number'
                      value={item.returnRate}
                      onChange={e =>
                        handleItemChange(
                          index,
                          'returnRate',
                          Number(e.target.value)
                        )
                      }
                    />
                    <TextInput
                      name={`quantity-${index}`}
                      label='Quantity'
                      type='number'
                      value={item.quantity}
                      onChange={e =>
                        handleItemChange(
                          index,
                          'quantity',
                          Number(e.target.value)
                        )
                      }
                    />
                    <TextInput
                      name={`returnDiscountAmt-${index}`}
                      label='Discount Amt'
                      type='number'
                      value={item.returnDiscountAmt}
                      onChange={e =>
                        handleItemChange(
                          index,
                          'returnDiscountAmt',
                          Number(e.target.value)
                        )
                      }
                    />
                    <TextInput
                      name={`returnVatAmt-${index}`}
                      label='VAT Amt'
                      type='number'
                      value={item.returnVatAmt}
                      onChange={e =>
                        handleItemChange(
                          index,
                          'returnVatAmt',
                          Number(e.target.value)
                        )
                      }
                    />
                    <TextInput
                      name={`returnCcAmt-${index}`}
                      label='CC Amt'
                      type='number'
                      value={item.returnCcAmt}
                      onChange={e =>
                        handleItemChange(
                          index,
                          'returnCcAmt',
                          Number(e.target.value)
                        )
                      }
                    />
                    <TextInput
                      name={`remark-${index}`}
                      label='Remark'
                      value={item.remark}
                      onChange={e =>
                        handleItemChange(index, 'remark', e.target.value)
                      }
                    />

                    {/* Read-only fields */}
                    <TextInput
                      name={`grnNo-${index}`}
                      label='GRN No'
                      value={item.grnNo}
                      disabled
                    />
                    <TextInput
                      name={`availableQty-${index}`}
                      label='Available Qty'
                      value={item.availableQty}
                      disabled
                    />
                    <TextInput
                      name={`itemRate-${index}`}
                      label='Item Rate'
                      value={item.itemRate}
                      disabled
                    />
                    <TextInput
                      name={`subtotal-${index}`}
                      label='Subtotal'
                      value={item.subtotal}
                      disabled
                    />
                    <TextInput
                      name={`totalAmount-${index}`}
                      label='Total Amount'
                      value={item.totalAmount}
                      disabled
                    />
                  </div>
                ))}
                {hasPermission('inventory', 'create') && (
                <CustomButton
                  type='button'
                  label={t('Add Item')}
                  icon={FaPlus}
                  onClick={handleAddItem}
                  className='ml-2'
                />
                )}
              </div>
            )}{' '}
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default ReturnToVendorForm
